import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, trackSection, trackCTA } from "../../lib/analytics";

/**
 * Site-wide analytics wiring. Non-visual.
 * - Fires a page_view on every public route change.
 * - Observes any DOM element that has `data-analytics-section="<key>"`
 *   and fires a section_view once when it scrolls 50%+ into view.
 * - Delegates clicks on elements marked `data-analytics-cta="<key>"`
 *   and fires a cta_click.
 * - Skips all care-portal / admin routes.
 */
export default function AnalyticsTracker() {
  const location = useLocation();
  const observerRef = useRef(null);

  // Page views + section observer wiring per route
  useEffect(() => {
    const path = location.pathname || "/";
    if (path.startsWith("/care-portal") || path.startsWith("/admin")) return;

    trackPageView(path);

    // Tear down any previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const el = entry.target;
          const key = el.getAttribute("data-analytics-section");
          if (key) trackSection(key, path);
          io.unobserve(el);
        }
      },
      { threshold: 0.5 }
    );
    observerRef.current = io;

    // Attach after paint so elements exist in DOM.
    const raf = requestAnimationFrame(() => {
      document
        .querySelectorAll("[data-analytics-section]")
        .forEach((el) => io.observe(el));
    });

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      observerRef.current = null;
    };
  }, [location.pathname]);

  // Global delegated CTA click tracking
  useEffect(() => {
    const handler = (e) => {
      const t = e.target.closest("[data-analytics-cta]");
      if (!t) return;
      const key = t.getAttribute("data-analytics-cta");
      if (key) trackCTA(key);
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  return null;
}
